import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Box, Skeleton, Tooltip } from '@chakra-ui/react';
import Header from '../header/Header';
import { useSelector, useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Input from './Input';
import { investors } from './data';
import classNames from 'classnames';
import HeaderSection from './HeaderSection';
import { useData } from '../../data';
import { useMutation, mutateFunction } from '../../libs/apis';
import { CustomToast } from '../toast/CustomToast';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import Loader from './Loader';
import UserService from '../../utils/UserService';
import { setCurrentTermSheetId } from '../../redux-toolkit/reducers/dealFlowSlice';
import NDAModal from '../modal/NDAModal';
import { FaInfoCircle } from 'react-icons/fa';
import BrandButton from '../brandButton/BrandButton';

const Content = ({ label, value }) => {
  return (
    <div className="flex flex-col gap-4">
      <label className="font-semibold">{label}</label>
      <p
        className={`text-sm text-[#17171780] ${value == 'Not provided' ? 'text-gray-300' : ''}`}
      >
        {value}
      </p>
    </div>
  );
};

const Sheet = ({
  selecetedInvestor,
  founder,
  type,
  actionType,
  termSheetData,
}) => {
  const SUBSCRIPTION_FLAG = process.env.REACT_APP_SUBSCRIPTION_FLAG;
  const dispatch = useDispatch();
  const accountType = useSelector(
    (state) => state?.user?.userInfo?.userTypeTitle,
  );
  const loggedInUserSubscription = useSelector(
    (state) => state?.user?.userSubscriptionInfo,
  );
  const currentPlanName = loggedInUserSubscription?.subscription_plan?.name;
  const isProPlan = currentPlanName?.trim().toLowerCase().includes('pro');
  const navigate = useNavigate();
  const { addToast } = CustomToast();
  const queryClient = useQueryClient();
  const currentDealFlow = useSelector(
    (state) => state?.dealFlow?.currentDealFlow,
  );


  const schema = yup.object({
    amountCommitted: yup
      .string()
      .required('Amount is required')
      .test(
        'min-ticket-size',
        `Amount committed cannot be less than minimum ticket size ${currentDealFlow?.min_ticket_size}`,
        function (value) {
          const minTicketSize = currentDealFlow?.min_ticket_size;
          if (!value || !minTicketSize) return true;
          const parsedValue = value.startsWith('$')
            ? value.substring(1).replace(/,/g, '')
            : value.replace(/,/g, '');
          return Number(parsedValue) >= Number(minTicketSize);
        },
      ),
    valuation: yup.string().optional(),
    stage: yup.string().optional(),
    investmentType: yup.string().optional(),
    investmentHistory: yup
      .number()
      .typeError('Investment history must be a number')
      .optional(),
    companyInvestedCount: yup
      .number()
      .typeError('Company count must be a number')
      .optional(),
    portfolioSize: yup.string().optional(),
    engagementStyle: yup.string().optional(),
    geographicFocus: yup.string().optional(),
    perferredSector: yup.string().optional(),
    acceptNDA: yup
      .boolean()
      .oneOf([true], 'Click on NDA to accept')
      .required('Click on NDA to accept'),
    // proofOfFund: yup.string().required('Proof of funds is required'),
  });

  const [isNDAModalOpen, setIsNDAModalOpen] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
    watch,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      stage: currentDealFlow?.stage,
      valuation: currentDealFlow?.company?.valuation,
      amountCommitted: termSheetData?.investment_amount || '',
      investmentType: termSheetData?.investment_type ?? 'Equity',
      investmentHistory: termSheetData?.investment_history ?? '4',
      companyInvestedCount: termSheetData?.company_invested_count ?? '4',
      portfolioSize: termSheetData?.portfolio_size ?? '',
      engagementStyle: termSheetData?.engagement_style ?? 'Equity',
      geographicFocus:
        termSheetData?.investor?.investment_location
          ?.map((item) => item?.name)
          ?.join(',') || 'Region',
      perferredSector:
        termSheetData?.investor?.business_category
          ?.map((item) => item?.title)
          ?.join(',') || 'Tech',
      acceptNDA:
        accountType === 'founder' ? true : !!termSheetData?.nda_accepted_at,
      // proofOfFund: termSheetData?.proof_of_funds?.[0] ?? '',
    },
    mode: 'onBlur',
  });

  useEffect(() => {
    reset({
      stage: currentDealFlow?.stage,
      valuation: currentDealFlow?.company?.valuation
        ? `$${Number(currentDealFlow?.company?.valuation)?.toLocaleString()}`
        : '',
      amountCommitted: termSheetData?.investment_amount
        ? `$${Number(termSheetData?.investment_amount)?.toLocaleString()}`
        : '',
      investmentType: termSheetData?.investment_type ?? 'Equity',
      investmentHistory: termSheetData?.investment_history ?? '4',
      companyInvestedCount: termSheetData?.company_invested_count ?? '4',
      portfolioSize: termSheetData?.portfolio_size ?? '',
      engagementStyle: termSheetData?.engagement_style ?? 'Equity',
      geographicFocus:
        termSheetData?.investor?.investment_location
          ?.map((item) => item?.name)
          ?.join(',') || 'Region',
      perferredSector:
        termSheetData?.investor?.business_category
          ?.map((item) => item?.title)
          ?.join(',') || 'Tech',
      acceptNDA:
        accountType === 'founder' ? true : !!termSheetData?.nda_accepted_at,
    });
  }, [currentDealFlow, termSheetData, reset]);

  const { mutate: createTermSheet, isLoading: createTermSheetLoading } =
    useMutation(mutateFunction);

  const { mutate: UpdateTermSheet, isLoading: updateTermSheetLoading } =
    useMutation(mutateFunction);

  const { mutate: signTermSheet, isLoading: signTermSheetLoading } =
    useMutation(mutateFunction);

  const { mutate: rejectTermSheet, isLoading: rehiectTermSheetLoading } =
    useMutation(mutateFunction);

  const companyInfo = useSelector((state) => state?.company?.companyInfo);

  const formRef = useRef(null);

  const acceptNDAValue = watch('acceptNDA');

  const handleButtonClick = (e) => {
    e.preventDefault();

    if (!acceptNDAValue) {
      addToast('Please Click on the Sign Here Link to accept T&Cs', 'warning');
    }

    formRef.current.requestSubmit();
  };

  const onSubmit = (data) => {
    if (accountType === 'investor') {
      // const formData = new FormData();

      // formData.append('investment_amount', data.amountCommitted);
      // formData.append('company_id', Number(companyInfo?.id));
      // formData.append('deal_id', currentDealFlow?.id);

      // Add the proof of funds image if it exists
      // const proofOfFundsInput = document.getElementById('proofOfFunds');
      // if (proofOfFundsInput?.files[0]) {
      //   formData.append('attachments', proofOfFundsInput.files[0]);
      // }

      const parsedAmountCommitted = data.amountCommitted.startsWith('$')
        ? data.amountCommitted.substring(1).replace(/,/g, '')
        : data.amountCommitted.replace(/,/g, '');

      const payload = {
        investment_amount: Number(parsedAmountCommitted),
        company_id: companyInfo?.id,
        deal_id: currentDealFlow?.id,
      };

      if (actionType === 'create') {
        createTermSheet(
          {
            key: 'termsheet/create',
            method: 'post',
            data: payload,
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          },
          {
            onSuccess: (res) => {
              queryClient.invalidateQueries(
                `termsheet/${currentDealFlow?.id}/participants`,
              );
              addToast(res);
            },
            onError: (err) => {
              addToast(
                err?.response?.data?.message || 'Error creating termsheet',
                'error',
              );
            },
          },
        );
      } else {
        UpdateTermSheet(
          {
            key: `termsheet/${termSheetData?.id}`,
            method: 'put',
            data: payload,
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          },
          {
            onSuccess: (res) => {
              queryClient.invalidateQueries(
                `termsheet/${currentDealFlow?.id}/participants`,
              );
              addToast(res);
            },
            onError: (err) => {
              addToast(
                err?.response?.data?.message || 'Error creating termsheet',
                'error',
              );
            },
          },
        );
      }
    } else {
      if (
        termSheetData &&
        (termSheetData?.status === 'signed' ||
          termSheetData?.status === 'rejected')
      ) {
        dispatch(setCurrentTermSheetId(termSheetData?.id));
        UserService.setCurrentTermSheetId(termSheetData?.id);
        navigate('../view-post-agreement-stage');
      } else {
        //sign term sheet
        signTermSheet(
          {
            key: `termsheet/${termSheetData?.id}/sign`,
            method: 'put',
          },
          {
            onSuccess: (res) => {
              queryClient.invalidateQueries(
                `termsheet/${currentDealFlow?.id}/participants`,
              );
              addToast(res);
            },
            onError: (err) => {
              addToast(
                err?.response?.data?.message || 'Error signing termsheet',
                'error',
              );
            },
          },
        );
      }
    }
  };

  const [previewUrl, setPreviewUrl] = useState(
    termSheetData?.proof_of_funds?.[0],
  );

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      setValue('proofOfFund', file, { shouldValidate: true });
    }
  };

  const handleRejectTermSheet = () => {
    rejectTermSheet(
      {
        key: `termsheet/${termSheetData?.id}/reject`,
        method: 'put',
      },
      {
        onSuccess: (res) => {
          queryClient.invalidateQueries(
            `termsheet/${currentDealFlow?.id}/participants`,
          );
          addToast(res);
        },
        onError: (err) => {
          addToast(
            err?.response?.data?.message || 'Error rejecting termsheet',
            'error',
          );
        },
      },
    );
  };

  const handleNDAAccept = () => {
    setValue('acceptNDA', true);
    setIsNDAModalOpen(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not provided';
    const date = new Date(dateString);
    if (isNaN(date)) return dateString;
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <>
      <NDAModal
        isOpen={isNDAModalOpen}
        onClose={() => setIsNDAModalOpen(false)}
        onAccept={handleNDAAccept}
        accept={
          accountType === 'founder' ||
          termSheetData?.status === 'rejected' ||
          acceptNDAValue
        }
      />

      <div className="flex items-center justify-center">
        <div className="w-full sm:w-[70%] md:w-[60%] lg:w-[70%] h-full flex flex-col">
          <form
            ref={formRef}
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            className="w-full flex-1 flex flex-col"
          >
            <div className="flex-1 rounded-xl overflow-y-auto hide-scroll">
              {/* Add Process Flow Guide */}
              {accountType === 'investor' && (
                <div className="mb-8 bg-white rounded-lg p-6 shadow-sm w-full">
                  <h3 className="text-lg font-semibold text-[#2F4B6E] mb-6">
                    Investment Process
                  </h3>
                  <div className="flex flex-col md:flex-row gap-6 md:gap-4 lg:gap-6">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-3 h-full">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#134A70] text-white flex items-center justify-center font-semibold text-sm">
                          1
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-[#2F4B6E] mb-2">
                            Commit Investment Amount
                          </h4>
                          <p className="text-sm text-gray-600 leading-relaxed">
                            Enter your investment commitment amount in the form
                            below. This amount must meet the minimum ticket size
                            requirement.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-3 h-full">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#134A70] text-white flex items-center justify-center font-semibold text-sm">
                          2
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-[#2F4B6E] mb-2">
                            Review & Sign Legal Documents
                          </h4>
                          <p className="text-sm text-gray-600 leading-relaxed">
                            Carefully review and sign the NDA and other legal
                            documents to proceed with your investment.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-3 h-full">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#134A70] text-white flex items-center justify-center font-semibold text-sm">
                          3
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-[#2F4B6E] mb-2">
                            Submit Commitment
                          </h4>
                          <p className="text-sm text-gray-600 leading-relaxed">
                            Review all details and submit your investment
                            commitment for the founding team's review.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div
                className={classNames({
                  'w-full flex': true,
                  'flex-col': type === 'investor',
                  'flex-col-reverse': type === 'founder',
                })}
              >
                {/* owner section */}
                <div
                  className={classNames({
                    'w-full bg-[#F4F7FD] p-4 md:p-6 rounded-md flex flex-col gap-4': true,
                    'mt-0': type === 'investor',
                    'mt-6': type === 'founder',
                  })}
                >
                  <h2 className="text-lg text-[#2F4B6E] font-bold">
                    Investor information
                  </h2>
                  <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Content
                      label="Name"
                      value={selecetedInvestor?.name || 'Not provided'}
                    />
                    <Content
                      label="Email"
                      value={selecetedInvestor?.email || 'Not provided'}
                    />
                    <Content
                      label="Phone no"
                      value={selecetedInvestor?.phone || 'Not provided'}
                    />
                    <Content
                      label="Company name"
                      value={
                        selecetedInvestor?.company?.title || 'Not provided'
                      }
                    />
                    <Content
                      label="Location"
                      value={
                        selecetedInvestor?.company?.incorporation_location
                          ?.map((item) => item?.name)
                          ?.join(',') || 'Not provided'
                      }
                    />
                  </div>
                </div>

                {/* guest section */}
                <div
                  className={classNames({
                    'w-full flex flex-col bg-[#F4F7FD] p-4 md:p-6 rounded-md gap-4': true,
                    'mt-0': type === 'founder',
                    'mt-6': type === 'investor',
                  })}
                >
                  <h2 className="text-lg text-[#2F4B6E] font-bold">
                    Founder information
                  </h2>
                  <div className="w-full grid grid-cols-1 md:grid-cols-2  gap-4">
                    <Content
                      label="Name"
                      value={founder.name || 'Not provided'}
                    />
                    <Content
                      label="Email"
                      value={founder.email || 'Not provided'}
                    />
                    <Content
                      label="Phone no"
                      value={founder.phone_number || 'Not provided'}
                    />
                    <Content
                      label="Company name"
                      value={founder.company?.title || 'Not provided'}
                    />
                    <Content
                      label="Location"
                      value={
                        founder.company?.incorporation_location
                          .map((item) => item?.name)
                          .join(',') || 'Not provided'
                      }
                    />
                  </div>
                </div>
              </div>

              <div className="w-full flex flex-col gap-6 mt-6">
                {/* Industry focus section */}
                <div className="w-full bg-[#F4F7FD] p-4 md:p-6 rounded-md flex flex-col gap-4">
                  <h2 className="text-lg text-[#2F4B6E] font-bold">
                    Industry & Focus
                  </h2>
                  <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label="Preferred Sector/industries"
                      placeholder="Technology"
                      type="text"
                      htmlFor="perferredSector"
                      error={errors.perferredSector?.message}
                      labelClassName="font-semibold"
                      disabled
                      {...register('perferredSector')}
                      className="border-none px-0 py-2 text-sm text-[#17171780]"
                    />

                    <Input
                      label="Geographic Focus"
                      placeholder="Region"
                      type="text"
                      htmlFor="geographicFocus"
                      error={errors.geographicFocus?.message}
                      labelClassName="font-semibold"
                      disabled
                      {...register('geographicFocus')}
                      className="border-none px-0 py-2 text-sm text-[#17171780]"
                    />
                  </div>
                </div>

                {/* Investment section */}
                <div className="w-full flex bg-[#F4F7FD] p-4 md:p-6 rounded-md flex-col gap-4">
                  <h2 className="text-lg text-[#2F4B6E] font-bold">
                    Investment Details
                  </h2>
                  <div className="w-full grid grid-cols-1 lg:grid-cols-2  gap-4">
                    <Content
                      label="Raise Name"
                      value={currentDealFlow?.name || 'Not provided'}
                    />

                    <Input
                      label="Current Valuation"
                      placeholder="$10,000,000"
                      type="text"
                      htmlFor="valuation"
                      labelClassName="font-semibold"
                      error={errors.valuation?.message}
                      disabled
                      {...register('valuation')}
                      className="border-none px-0 py-2 text-sm text-[#17171780]"
                    />

                    {/* <Content label="Current Number of Investors" value={'Not provided'} /> */}

                    <Content
                      label="Target Amout to Raise"
                      value={
                        currentDealFlow?.target_amount
                          ? `$${currentDealFlow?.target_amount}`
                          : 'Not provided'
                      }
                    />

                    <Content
                      label="Minimum Investment Ticket"
                      value={
                        currentDealFlow?.min_ticket_size
                          ? `${currentDealFlow?.min_ticket_size}`
                          : 'Not provided'
                      }
                    />

                    <Content
                      label="Fundraising Close Date"
                      value={formatDate(currentDealFlow?.close_date)}
                    />

                    <Input
                      label="Stage"
                      placeholder="Seed"
                      htmlFor="stage"
                      error={errors.stage?.message}
                      labelClassName="font-semibold"
                      disabled
                      {...register('stage')}
                      className="border-none px-0 py-2 text-sm text-[#17171780]"
                    />

                    <Input
                      label="Investment Type"
                      placeholder="Equity"
                      htmlFor="investmentType"
                      error={errors.investmentType?.message}
                      labelClassName="font-semibold"
                      disabled
                      {...register('investmentType')}
                      className="border-none px-0 py-2 text-sm text-[#17171780]"
                    />
                  </div>
                </div>

                {/* Experience section */}
                {/* <div className="w-full flex flex-col gap-4">
                  <h2 className="text-lg text-[#2F4B6E] font-bold">
                    Investor Experience
                  </h2>
                  <div className="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <Input
                      label="Investment History"
                      placeholder="5"
                      type="number"
                      htmlFor="investmentHistory"
                      error={errors.investmentHistory?.message}
                      disabled
                      {...register('investmentHistory')}
                      className="border-none px-0 py-2"
                    />

                    <Input
                      label="Company Invested In"
                      placeholder="4"
                      type="number"
                      htmlFor="companyInvestedCount"
                      error={errors.companyInvestedCount?.message}
                      disabled
                      {...register('companyInvestedCount')}
                      className="border-none px-0 py-2"
                    />

                    <Input
                      label="Portfolio Size"
                      placeholder="Optional"
                      htmlFor="portfolioSize"
                      error={errors.portfolioSize?.message}
                      disabled
                      {...register('portfolioSize')}
                      className="border-none px-0 py-2"
                    />

                    <Input
                      label="Engagement Style"
                      placeholder="Equity"
                      htmlFor="engagementStyle"
                      error={errors.engagementStyle?.message}
                      disabled
                      {...register('engagementStyle')}
                      className="border-none px-0 py-2"
                    />
                  </div>
                </div> */}

                {/* Industry focus section */}
                <div className="w-full flex bg-[#F4F7FD] p-4 md:p-6 rounded-md flex-col gap-4">
                  <div className="flex gap-2 items-center">
                    <h2 className="text-lg text-[#2F4B6E] font-bold">
                      Amount Committed
                    </h2>
                    <Tooltip label="This is the amount you are indicating interest in investing. It is non-binding and helps the founder gauge overall investor interest.">
                      <span className="text-gray-500 cursor-help">ⓘ</span>
                    </Tooltip>
                  </div>
                  <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label="Amount"
                      placeholder="$1,000,000"
                      type="text"
                      htmlFor="amountCommitted"
                      error={errors.amountCommitted?.message}
                      disabled={
                        type === 'founder' ||
                        termSheetData?.status === 'rejected'
                      }
                      {...register('amountCommitted')}
                      className="bg-white"
                    />
                  </div>
                </div>

                {/* Legal compliance section */}
                <div className="w-full bg-[#F4F7FD] p-4 md:p-6 rounded-md flex flex-col gap-4">
                  <h2 className="text-lg text-[#2F4B6E] font-bold">
                    Legal and Compliance
                  </h2>
                  <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8 lg:gap-20">
                    {/* NDA section */}
                    <div className="flex flex-col gap-4">
                      {type === 'founder' ? (
                        <p>
                          You can view the NDA{' '}
                          <a
                            type="button"
                            onClick={() => setIsNDAModalOpen(true)}
                            className="cursor-pointer"
                          >
                            here
                          </a>
                        </p>
                      ) : (
                        <a
                          onClick={() => setIsNDAModalOpen(true)}
                          className={`cursor-pointer text-left ${errors.acceptNDA?.message ? 'text-red-500' : ''}`}
                        >
                          {errors.acceptNDA?.message
                            ? 'Click to Read and Sign NDA'
                            : 'Read and Sign Here'}
                        </a>
                      )}

                      {acceptNDAValue && (
                        <Input
                          label="NDAs"
                          placeholder="Region"
                          type="checkbox"
                          htmlFor="acceptNDA"
                          error={errors.acceptNDA?.message}
                          className="w-6 h-6 border-2 border-[#2F4B6E] bg-transparent"
                          // {...register('acceptNDA')}
                          checked={acceptNDAValue}
                          disabled={
                            type === 'founder' ||
                            !!termSheetData?.nda_accepted_at
                          }
                          readOnly
                        />
                      )}
                    </div>

                    <div className="flex flex-col gap-4">
                      <label>Accredited investor Status</label>

                      <select className="w-full bg-white outline-none border border-[#00000033] rounded p-2 placeholder:text-[#17171780] cursor-pointer">
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </select>
                    </div>

                    {/* <div className="flex flex-col gap-4">
                  <label>Proof of funds</label>
                  <div className="max-w-[200px] max-h-[200px] w-32 h-32 border-2 border-dashed border-[#4848484D] rounded-xl flex flex-col items-center justify-center cursor-pointer bg-[#C7DFF0] overflow-hidden relative">
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      id="proofOfFunds"
                      onChange={handleFileChange}
                      disabled={type === 'founder'}
                    />
                    {previewUrl ? (
                      <div className="relative w-full h-full group">
                        <img
                          src={previewUrl}
                          alt="Preview"
                          className="rounded-xl w-full h-full object-cover"
                        />

                        {accountType === 'investor' && (
                          <label
                            htmlFor="proofOfFunds"
                            className="absolute cursor-pointer inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                          >
                            <div className="text-white flex flex-col items-center">
                              <svg
                                className="w-8 h-8 mb-1"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                                />
                              </svg>
                              <span className="text-sm">Change Photo</span>
                            </div>
                          </label>
                        )}
                      </div>
                    ) : (
                      <label
                        htmlFor="proofOfFunds"
                        className="p-1.5 cursor-pointer flex flex-col items-center"
                      >
                        <svg
                          className="w-12 h-16 text-gray-400 mb-1"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                        <p className="text-sm text-center text-gray-500">
                          Add or drag photo
                        </p>
                      </label>
                    )}
                  </div>

                  {errors.proofOfFund && (
                    <span className="text-red-500 text-sm">
                      {errors.proofOfFund.message}
                    </span>
                  )}
                </div> */}
                    {currentDealFlow?.data_room_link && (
                      <div>
                        <a
                          href={currentDealFlow?.data_room_link}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Data Room Link
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {accountType === 'investor' ? (
              !(termSheetData?.status === 'rejected') ? (
                <div>
                  {SUBSCRIPTION_FLAG === "on" && !isProPlan ? (
                    actionType === 'create' ? (
                      <button
                        type="button"
                        className="w-full py-2.5 my-5 text-white rounded bg-[#134A70] hover:bg-[#0d3654] transition-colors"
                        onClick={() => navigate(`${window.location.pathname}?upgrade=true`, { replace: true })}
                      >
                        Upgrade to Pro Plan to Submit
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="w-full flex items-center justify-center py-2.5 my-5 text-white rounded bg-[#134A70] hover:bg-[#0d3654] transition-colors"
                        onClick={() => navigate(`${window.location.pathname}?upgrade=true`, { replace: true })}
                      >
                        Upgrade to Pro Plan to Update
                      </button>
                    )
                  ) : (
                    actionType === 'create' ? (
                      <button
                        type="submit"
                        className="w-full py-2.5 my-5 text-white rounded bg-[#134A70] hover:bg-[#0d3654] transition-colors"
                        onClick={handleButtonClick}
                      >
                        {createTermSheetLoading ? 'Submitting...' : 'Submit'}
                      </button>
                    ) : (
                      <button
                        type="submit"
                        className="w-full flex items-center justify-center py-2.5 my-5 text-white rounded bg-[#134A70] hover:bg-[#0d3654] transition-colors"
                      >
                        {updateTermSheetLoading ? (
                          'updating....'
                        ) : (
                          <span>Term Sheet Sent • Click to Update</span>
                        )}
                      </button>
                    )
                  )}
                </div>
              ) : null
            ) : (
              <div className="w-full">
                {termSheetData?.status === 'signed' ||
                  termSheetData?.status === 'rejected' ? (
                  <button className="w-full py-2.5 my-5 text-white rounded bg-[#134A70] hover:bg-[#0d3654] transition-colors">
                    Next
                  </button>
                ) : (
                  <div className="w-full flex items-center gap-4">
                    <button
                      type="submit"
                      className="w-full py-2.5 my-5 text-white rounded bg-[#134A70] hover:bg-[#0d3654] transition-colors"
                    >
                      {signTermSheetLoading ? 'Signing...' : 'Accept'}
                    </button>

                    <button
                      type="button"
                      className="w-full py-2.5 my-5 text-white rounded bg-[#DC3545] hover:bg-[#b42c3a] transition-colors"
                      onClick={(e) => {
                        e.preventDefault();
                        handleRejectTermSheet();
                      }}
                    >
                      {rehiectTermSheetLoading ? 'Rejecting...' : 'Reject'}
                    </button>
                  </div>
                )}
              </div>
            )}
          </form>
        </div>
      </div>
    </>
  );
};

const TermSheet = () => {
  const dispatch = useDispatch();
  const userInfo = useSelector((state) => state?.user?.userInfo);

  const companyInfo = useSelector((state) => state?.company?.companyInfo);

  const { addToast } = CustomToast();

  const allCompanyInfo = useSelector(
    (state) => state?.allcompanies?.allCompaniesInfo,
  );

  const accountType = userInfo?.userTypeTitle;

  const navigate = useNavigate();
  const currentDealFlow = useSelector(
    (state) => state?.dealFlow?.currentDealFlow,
  );

  const currentTermSheetId = useSelector(
    (state) => state?.dealFlow?.currentTermSheetId,
  );

  const [selectedInvestor, setSelectedInvestor] = useState(
    accountType === 'investor'
      ? {
        name: `${userInfo?.firstName} ${userInfo?.lastName}`,
        email: userInfo?.email,
        phone: userInfo?.phone_number ?? '',
        company: companyInfo,
      }
      : investors[0],
  );

  // const [showDealSheet, setShowDealSheet] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState();
  const [dealParticipantsData, setDealParticipantsData] = useState([]);
  const [selectedTermSheet, setSelectedTermSheet] = useState({});
  const [investorInfos, setInvestorInfo] = useState([]);

  const founderInfo = useMemo(
    () => ({
      name: `${selectedDeal?.user?.first_name} ${selectedDeal?.user?.last_name}`,
      email: selectedDeal?.user?.email ?? '',
      phone: selectedDeal?.user?.phone_number ?? '',
      company: selectedDeal?.company,
    }),
    [selectedDeal],
  );

  const {
    data: dealParticipants,
    isLoading: dealParticipantsLoading,
    isError: dealParticipantsError,
  } = useData(`termsheet/${currentDealFlow?.id}/participants`, 0, {});

  const {
    data: deal,
    isLoading: dealLoading,
    isError: dealError,
    refetch: refetchDealFlow,
  } = useData(`deal-flow/${currentDealFlow?.id}`, 0, {}, 1, true);

  useEffect(() => {
    if (deal && !dealLoading && !dealError) {
      setSelectedDeal(deal?.data?.deal);
    }
  }, [deal, dealLoading, dealError]);

  useEffect(() => {
    const verifyCompanyInfo = () => {
      // Skip verification for founders
      if (accountType === 'founder') return;

      // Check if company info exists and has required fields
      if (!companyInfo || !companyInfo.id) {
        addToast(
          'Please complete your company profile in Settings before proceeding.',
          'warning',
          'top',
          5000,
        );
        navigate('/account-settings');
        return;
      }

      // Check for required company fields
      const requiredFields = {
        title: 'Company Name',
        incorporation_location: 'Company Location',
      };

      const missingFields = Object.entries(requiredFields)
        .filter(
          ([field]) =>
            !companyInfo[field] ||
            (Array.isArray(companyInfo[field]) &&
              companyInfo[field].length === 0),
        )
        .map(([_, label]) => label);

      if (missingFields.length > 0) {
        addToast(
          `Please complete the following in your company profile: ${missingFields.join(', ')}`,
          'warning',
          'top',
          5000,
        );
        navigate('/account-settings');
      }
    };

    verifyCompanyInfo();
  }, [companyInfo, accountType, navigate, addToast]);

  useEffect(() => {
    if (
      dealParticipants &&
      !dealParticipantsLoading &&
      !dealParticipantsError
    ) {
      setDealParticipantsData(dealParticipants?.data?.termSheets);

      if (accountType === 'founder') {
        const termSheet = dealParticipants?.data?.termSheets?.find(
          (ts) => ts?.id == currentTermSheetId,
        );

        setSelectedTermSheet(termSheet);

        setInvestorInfo(
          dealParticipants?.data?.termSheets?.map((termSheet) => {
            return {
              id: termSheet.user_id,
              ...termSheet?.investor,
            };
          }),
        );

        const investor = termSheet?.investor;
        setSelectedInvestor({
          id: termSheet?.user_id,
          name: `${investor?.user?.first_name} ${investor?.user?.last_name}`,
          email: investor?.user?.email ?? '',
          phone: investor?.user?.phone_number ?? '',
          company: investor ?? null,
        });
      } else {
        // get the logged in investor termsheet from the list of termsheets for the deal
        const investorTermSheet = dealParticipants?.data?.termSheets?.find(
          (termSheet) => termSheet?.user_id === userInfo?.id,
        );

        if (
          accountType === 'investor' &&
          investorTermSheet &&
          investorTermSheet?.status === 'signed'
        ) {
          dispatch(setCurrentTermSheetId(investorTermSheet?.id));
          UserService.setCurrentTermSheetId(investorTermSheet?.id);
          navigate('../view-post-agreement-stage');
        }

        setSelectedTermSheet({
          name: `${userInfo?.firstName} ${userInfo?.lastName}`,
          email: userInfo?.email,
          phone: userInfo?.phone_number ?? '',
          company: userInfo?.company ?? null,
          ...investorTermSheet,
        });
      }
    }
  }, [dealParticipants, dealParticipantsLoading, dealParticipantsError]);

  const hasSubmittedTermSheet = dealParticipantsData?.some(
    (termSheet) => termSheet?.user_id === userInfo?.id,
  );

  const goToFounder = () => {
    navigate(`/view-user?userid=${selectedDeal?.user_id}&usertype=1`)
  }

  return (
    <Box>
      <Box className="h-screen flex flex-col">
        <Header title={'Term sheet'} />

        <Box className="flex-1 bg-gray-100 overflow-hidden">
          <Box className="h-full px-5 pt-4">
            {accountType === 'founder' && dealParticipantsData?.length < 1 ? (
              <div className="h-full flex flex-col items-center justify-center p-8 bg-white rounded-lg">
                <div className="flex flex-col items-center gap-4 max-w-md text-center">
                  {/* Document Icon */}
                  <svg
                    className="w-20 h-20 text-gray-300"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>

                  <h3 className="text-xl font-semibold text-[#2F4B6E]">
                    {accountType === 'founder'
                      ? 'Awaiting Term Sheets'
                      : 'No Term Sheet Submitted'}
                  </h3>

                  <p className="text-gray-600">
                    {accountType === 'founder'
                      ? "This deal flow is waiting for investor term sheets. You'll be notified when investors submit their proposals."
                      : "You haven't submitted a term sheet for this deal flow yet. Create a term sheet to propose your investment terms."}
                  </p>
                  {/* 
                  {accountType === 'investor' && (
                    <button
                      onClick={() => setShowDealSheet(true)}
                      className="mt-4 px-6 py-2.5 bg-[#134A70] text-white rounded hover:bg-[#0d3654] transition-colors"
                    >
                      Create Term Sheet
                    </button>
                  )} */}
                </div>
              </div>
            ) : (
              <Box className="h-full bg-white rounded-lg p-4 flex flex-col">
                {/* Fixed header section */}
                <HeaderSection
                  title={
                    accountType === 'founder'
                      ? selectedInvestor?.name
                      : founderInfo?.name
                  }
                  subTitle="Term Sheet Form"
                  checkboxState={selectedTermSheet?.status ?? 'in_progress'}
                />


                {accountType === 'investor' && (
                  <div className="mb-4 py-4 flex w-full justify-end rounded-lg">
                    <BrandButton
                      fontSize="13px"
                      width="200px"
                      className="p-3"
                      text={`View Founder's Profile`}
                      colorScheme="blue"
                      onClick={goToFounder}
                    />
                  </div>
                )}

                {/* Add status message for investors */}
                {accountType === 'investor' &&
                  !!selectedTermSheet?.nda_accepted_at &&
                  !selectedTermSheet?.rejected_at &&
                  !selectedTermSheet?.signed_at && (
                    <div className="mt-4 p-4 bg-[#F4F7FD] border border-[#E5E7EB] rounded-lg">
                      <div className="flex flex-col">
                        <h3 className="text-[#2F4B6E] font-semibold">
                          Term Sheet Under Review
                        </h3>
                        <p className="text-gray-600 text-sm mt-1">
                          Your term sheet has been successfully submitted and is
                          currently under review by the founding team. You will
                          be notified once they make their decision.
                        </p>
                      </div>
                    </div>
                  )}

                {/* Scrollable sheet section */}
                <Box className="flex flex-col-reverse md:flex-row gap-4 mt-6 flex-1 overflow-hidden">
                  <Box className="flex-1 overflow-y-auto hide-scroll">
                    <Sheet
                      selecetedInvestor={selectedInvestor}
                      founder={founderInfo}
                      type={accountType}
                      actionType={!hasSubmittedTermSheet ? 'create' : 'update'}
                      termSheetData={selectedTermSheet}
                    />
                  </Box>

                  {accountType === 'founder' && (
                    <div className="md:w-[200px] overflow-y-auto hide-scroll pr-2">
                      <div className="flex flex-row md:flex-col gap-2 pb-4">
                        {dealParticipantsLoading
                          ? // Chakra UI Skeleton loading items
                          [...Array(8)].map((_, index) => (
                            <Skeleton
                              key={index}
                              className="w-full px-4 py-2 rounded-lg flex-shrink-0"
                              height="36px"
                            />
                          ))
                          : // Actual investor items
                          investorInfos.map((investor) => {
                            // Find the corresponding termsheet to get status
                            const termsheet = dealParticipantsData.find(
                              (sheet) => sheet.user_id === investor.id,
                            );
                            const status = termsheet?.status || 'in_progress';

                            // Define status indicator styles
                            const statusColors = {
                              signed: 'bg-green-500',
                              in_progress: 'bg-yellow-500',
                              rejected: 'bg-red-500',
                            };

                            return (
                              <div
                                onClick={() => {
                                  setSelectedInvestor({
                                    id: investor.id,
                                    name: `${investor?.user?.first_name} ${investor?.user?.last_name}`,
                                    email: investor?.user?.email ?? '',
                                    phone: investor?.user?.phone_number ?? '',
                                    company: investor ?? null,
                                  });

                                  setSelectedTermSheet(
                                    dealParticipantsData.find(
                                      (termSheet) =>
                                        termSheet?.user_id === investor.id,
                                    ),
                                  );
                                }}
                                key={investor.id}
                                className={`
                                    w-full px-4 py-2 cursor-pointer rounded-lg flex-shrink-0 relative
                                    ${selectedInvestor?.id === investor.id
                                    ? 'bg-[#134A70] text-white border-[#134A70]'
                                    : 'bg-white border border-[#E5E7EB] hover:border-[#134A70]'
                                  }
                                    transition-all duration-200
                                  `}
                              >
                                {/* Status indicator dot */}
                                <div
                                  className={`absolute top-2 right-2 w-2.5 h-2.5 rounded-full ${statusColors[status] || 'bg-gray-300'}`}
                                  title={status.replace('_', ' ')}
                                />

                                <p className="text-center whitespace-nowrap truncate text-sm pr-4">
                                  {`${investor?.user?.first_name} ${investor?.user?.last_name}`}
                                </p>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {dealParticipantsLoading && !dealParticipantsError && <Loader />}
    </Box>
  );
};

export default TermSheet;
